// Affaires d'un partenaire abonné (spec § 3, organisations) : « Mes affaires » (pipeline de son organisation),
// « Transmises » (affaires reçues d'une organisation hôte, à ses étapes) et « À faire » (prochaines actions de tous les
// pipelines, la plus proche d'abord).
import { useGetList, useStore, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, PartnerOrg } from "../types";
import { NextAction } from "./DealFollowUp";
import { todoItems } from "./partnerOrg";
import {
  chosenHost,
  type HostChip,
  hostChips,
  pipelineForDeal,
} from "./partnerships";
import { useReadableOrgs } from "./usePartnerOrg";

export type PartnerDealTab = "mine" | "transmitted" | "todo";

export const usePartnerDealTab = () =>
  useStore<PartnerDealTab>("deals.partnerTab", "mine");

/** Hôte choisi dans l'onglet « Transmises » (organisation qui transmet) ; `null` tant que rien n'est choisi. */
export const usePartnerDealHost = () =>
  useStore<number | null>("deals.partnerHost", null);

/**
 * Onglet « Transmises » : affaires reçues d'organisations hôtes, une puce par hôte, l'hôte choisi (ou le premier si
 * le choix a disparu) et son organisation. Ne lit rien tant que `enabled` est faux.
 */
export const useTransmittedHosts = (
  myOrgId: number | null | undefined,
  enabled: boolean,
) => {
  const [stored, setStored] = usePartnerDealHost();
  const orgs = useReadableOrgs();
  // Même requête que `useReadableOrgs` (partagée par le cache) : sert à savoir si elle est encore en cours.
  const { isPending: orgsPending } = useGetList("partner_orgs", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: deals, isPending } = useGetList<Deal>(
    "deals",
    {
      filter: { "org_id@neq": myOrgId, "archived_at@is": null },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "next_action_date", order: "ASC" },
    },
    { enabled: enabled && myOrgId != null },
  );
  const today = new Date().toLocaleDateString("en-CA");
  const chips =
    myOrgId != null ? hostChips(deals ?? [], orgs ?? [], myOrgId, today) : [];
  const host = chosenHost(chips, stored);
  return {
    chips,
    host,
    setHost: setStored,
    // Organisation de l'hôte choisi ; l'équipe de la plateforme garde le pipeline du client.
    hostOrg: orgs?.find((o) => o.id === host),
    // Une lecture en échec n'est plus en attente : le tableau s'affiche sans puces plutôt que d'attendre sans fin.
    isPending: enabled && (isPending || orgsPending),
  };
};

/** Rangée de puces des hôtes (une seule puce : rien à choisir, rien n'est affiché). Retour à la ligne sur téléphone. */
export const HostChips = ({
  chips,
  host,
  onChange,
}: {
  chips: HostChip[];
  host: number | null;
  onChange: (orgId: number) => void;
}) => {
  const translate = useTranslate();
  if (chips.length < 2) return null;
  return (
    <div
      role="group"
      aria-label={translate("resources.deals.org.hosts_label")}
      className="flex flex-wrap gap-2 mt-3"
    >
      {chips.map((chip) => (
        <button
          key={chip.orgId}
          type="button"
          className="flex min-h-11 items-center md:min-h-0"
          aria-pressed={chip.orgId === host}
          onClick={() => onChange(chip.orgId)}
        >
          <Badge
            variant={chip.orgId === host ? "default" : "outline"}
            className="cursor-pointer gap-1.5 py-1"
          >
            {chip.name} · {chip.count}
            {chip.overdue && (
              <span
                className="size-2 rounded-full bg-destructive"
                title={translate("resources.deals.org.host_overdue")}
              />
            )}
          </Badge>
        </button>
      ))}
    </div>
  );
};

export const PartnerDealTabs = ({
  tab,
  onChange,
}: {
  tab: PartnerDealTab;
  onChange: (tab: PartnerDealTab) => void;
}) => {
  const translate = useTranslate();
  return (
    <Tabs value={tab} onValueChange={(v) => onChange(v as PartnerDealTab)}>
      <TabsList>
        <TabsTrigger value="mine">
          {translate("resources.deals.org.tab_mine")}
        </TabsTrigger>
        <TabsTrigger value="transmitted">
          {translate("resources.deals.org.tab_transmitted")}
        </TabsTrigger>
        <TabsTrigger value="todo">
          {translate("resources.deals.org.tab_todo")}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
};

/** Prochaines actions de tous les pipelines, avec le nom du pipeline de chaque affaire. */
export const DealTodo = ({ org }: { org: PartnerOrg | undefined }) => {
  const orgs = useReadableOrgs();
  const translate = useTranslate();
  const { dealStages, dealClosedStages } = useConfigurationContext();
  const client = { dealStages, dealClosedStages };
  const { data: deals, isPending } = useGetList<Deal>("deals", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "next_action_date", order: "ASC" },
  });
  if (isPending) return null;
  const today = new Date().toLocaleDateString("en-CA");
  const items = todoItems(deals ?? [], org, orgs ?? [], client, today);
  if (!items.length)
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {translate("resources.deals.org.todo_empty")}
      </p>
    );
  return (
    <div className="flex flex-col gap-2 pb-16 max-w-4xl">
      {items.map(({ deal, own }) => {
        const pipeline = pipelineForDeal(deal, org, orgs ?? [], client);
        return (
          <Link key={deal.id} to={`/deals/${deal.id}/show`}>
            <Card className="py-3 hover:bg-muted/50 active:bg-muted">
              <CardContent className="flex flex-col gap-1 px-4">
                <div className="flex flex-wrap items-start gap-2">
                  <span className="flex-1 min-w-0 font-medium leading-snug line-clamp-2">
                    {deal.name}
                  </span>
                  <Badge variant="outline">
                    {own
                      ? (org?.name ?? translate("resources.deals.org.tab_mine"))
                      : translate("resources.deals.org.tab_transmitted")}
                    {" · "}
                    {findDealLabel(pipeline.dealStages, deal.stage) ??
                      deal.stage}
                  </Badge>
                </div>
                <NextAction deal={deal} />
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
};
